const isDev = import.meta.env.DEV || 
  window.location.hostname === 'localhost' || 
  window.location.hostname === '127.0.0.1' || 
  window.location.hostname === '::1' || 
  window.location.hostname.startsWith('192.168.') || 
  window.location.hostname.startsWith('10.') || 
  window.location.hostname.startsWith('172.');

export const API_BASE = import.meta.env.VITE_API_URL || (isDev ? 'http://localhost:8080' : window.location.origin);
export const API_URL = `${API_BASE}/api`;

export const getToken = () => localStorage.getItem('token') || '';

export async function apiGet(path: string) {
  const res = await fetch(`${API_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
  });
  const data = await safeJson(res);
  if (res.status === 402) {
    if (window.location.pathname !== '/subscription/plans') {
      window.location.href = '/subscription/plans';
    }
  }
  if (!res.ok) {
    let msg = data?.error || 'Erro na API';
    if (data?.messages && typeof data.messages === 'object') {
      msg = Object.values(data.messages).join(', ');
    } else if (typeof data?.message === 'string') {
      msg = data.message;
    }
    throw { status: res.status, message: msg };
  }
  return data;
}

export async function apiPost(path: string, body: any) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify(body),
  });
  const data = await safeJson(res);
  if (res.status === 402) {
    if (window.location.pathname !== '/subscription/plans') {
      window.location.href = '/subscription/plans';
    }
  }
  if (!res.ok) {
    let msg = data?.error || 'Erro na API';
    if (data?.messages && typeof data.messages === 'object') {
      msg = Object.values(data.messages).join(', ');
    } else if (typeof data?.message === 'string') {
      msg = data.message;
    }
    throw { status: res.status, message: msg, data };
  }
  return data;
}

// Para endpoints que respondem com um arquivo (ex: download de zip) em vez de JSON.
export async function apiPostBlob(path: string, body: any): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw { status: res.status, message: data?.error || 'Erro na API' };
  }
  const disposition = res.headers.get('content-disposition') || '';
  const match = disposition.match(/filename="?([^";]+)"?/);
  const filename = match ? match[1] : 'download';
  const blob = await res.blob();
  return { blob, filename };
}

// GET de arquivo protegido (ex.: anexo do cliente) — o token vai no header, não na URL.
export async function apiGetBlob(path: string): Promise<{ blob: Blob; filename: string }> {
  const res = await fetch(`${API_URL}${path}`, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!res.ok) {
    const data = await safeJson(res);
    throw { status: res.status, message: data?.messages?.error || data?.error || 'Erro ao baixar o arquivo' };
  }
  const disposition = res.headers.get('content-disposition') || '';
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/);
  const plain = disposition.match(/filename="?([^";]+)"?/);
  // Vazio quando o header não vem (em dev, API em outra origem não expõe Content-Disposition): quem chama usa o nome que já conhece
  const filename = utf8 ? decodeURIComponent(utf8[1]) : plain ? plain[1] : '';
  return { blob: await res.blob(), filename };
}

export async function apiPostForm(path: string, form: FormData) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: form,
  });
  const data = await safeJson(res);
  if (!res.ok) {
    let msg = data?.error || 'Erro na API';
    if (data?.messages && typeof data.messages === 'object') {
      msg = Object.values(data.messages).join(', ');
    } else if (typeof data?.message === 'string') {
      msg = data.message;
    }
    throw { status: res.status, message: msg, data };
  }
  return data;
}

export async function apiPut(path: string, body: any) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify(body),
  });
  const data = await safeJson(res);
  if (res.status === 402) {
    if (window.location.pathname !== '/subscription/plans') {
      window.location.href = '/subscription/plans';
    }
  }
  if (!res.ok) {
    let msg = data?.error || 'Erro na API';
    if (data?.messages && typeof data.messages === 'object') {
      msg = Object.values(data.messages).join(', ');
    } else if (typeof data?.message === 'string') {
      msg = data.message;
    }
    throw { status: res.status, message: msg };
  }
  return data;
}

export async function apiPutForm(path: string, form: FormData) {
  form.append('_method', 'PUT');
  const res = await fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}` },
    body: form,
  });
  const data = await safeJson(res);
  if (!res.ok) {
    let msg = data?.error || 'Erro na API';
    if (data?.messages && typeof data.messages === 'object') {
      msg = Object.values(data.messages).join(', ');
    } else if (typeof data?.message === 'string') {
      msg = data.message;
    }
    throw { status: res.status, message: msg, data };
  }
  return data;
}

export async function apiDelete(path: string, body?: any) {
  const res = await fetch(`${API_URL}${path}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await safeJson(res);
  if (!res.ok) throw { status: res.status, message: data?.error || 'Erro na API' };
  return data;
}

export function resolveAssetUrl(path?: string) {
  if (!path) return '';
  
  let cleanPath = path;
  // Se a URL contiver localhost ou 127.0.0.1, removemos o host para tratar como caminho relativo
  if (cleanPath.includes('localhost:') || cleanPath.includes('127.0.0.1:')) {
    const match = cleanPath.match(/https?:\/\/[^\/]+(\/.*)/);
    if (match) {
      cleanPath = match[1];
    }
  }

  if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) return cleanPath;
  if (cleanPath.startsWith('/')) return `${API_BASE}${cleanPath}`;
  return cleanPath;
}

async function safeJson(res: Response) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

